/**
 * knowledgeBase 页面业务逻辑层
 * 包含 data、computed、methods，通过 mixin 注入到视图组件
 */
import { aiSearch, aiQA, askStream } from '@/api/knowledge';
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import { translate, SUPPORTED_LOCALES, DEFAULT_LOCALE, STORAGE_KEY } from '@/i18n';

// markdown-it 实例：html:true 保留 AI 返回的内嵌引用标签，linkify 识别裸链接，breaks 保留换行
const md = new MarkdownIt({
  html: true,
  linkify: true,
  breaks: true,
});

// 乐享页面完整域名：参考文档相对路径（/pages/...）补全跳转用
const LEXIANG_ORIGIN = 'https://kb.tek.cn';

// AI 问答会话有效期：最新会话时间（毫秒时间戳）距当前超过 1 整天则视为过期，发起会话时会话 ID 置空
const SESSION_MAX_AGE = 24 * 60 * 60 * 1000;
// 最新会话时间存储 key（毫秒时间戳），与会话 ID（tek-qa-session）一同记录在 localStorage
const SESSION_TIME_KEY = 'tek-qa-session-time';
// SSO 模式独立会话 key：避免与 AI 问答页会话串号（SSO 走 /lexiang/ai/sso/stream）
const SESSION_KEY_SSO = 'tek-qa-session-sso';
const SESSION_TIME_KEY_SSO = 'tek-qa-session-time-sso';

// markdown-it 生成的链接统一在新标签页打开；
// 参考文档相对路径链接（/pages/xxx）优先替换为 downloadUrl（真实 COS 下载链接），
// downloadUrl 缺失时补全乐享域名跳转；都不可用时降级为纯文本（仅显示标题，不可点击）
const defaultLinkOpen = md.renderer.rules.link_open || function(tokens, idx, options, env, self) {
  return self.renderToken(tokens, idx, options);
};
md.renderer.rules.link_open = function(tokens, idx, options, env, self) {
  const token = tokens[idx];
  const href = token.attrGet('href');
  // SSO 模式：技能文件含敏感信息，内部文档相对路径（/pages/...）链接降级为纯文本，不暴露内部文件 URL；
  // 外部网址（http(s)/mailto/tel 等）保持可点击超链接，AI 回复中的官网/工单等链接需可跳转
  const sso = !!(env && env.sso);
  if (href && !href.startsWith('#')) {
    let finalHref = null;
    if (sso && (href.startsWith('/pages/') || href.startsWith('pages/'))) {
      // 纯文本降级：<a> 换成 <span>，移除 href，仅显示标题、不可点击
      token.tag = 'span';
      const hrefIdx = token.attrIndex('href');
      if (hrefIdx !== -1) token.attrs.splice(hrefIdx, 1);
      for (let i = idx + 1; i < tokens.length; i += 1) {
        if (tokens[i].type === 'link_close') {
          tokens[i].tag = 'span';
          break;
        }
      }
      return defaultLinkOpen(tokens, idx, options, env, self);
    }
    if (href.startsWith('/pages/') || href.startsWith('pages/')) {
      // 参考文档相对路径：从当前消息的 sources 中匹配，取真实下载链接
      const sources = (env && env.sources) || [];
      const norm = (p) => String(p || '').replace(/^\/+|\/+$/g, '');
      const doc = sources.find((s) => s.url && (s.url === href || norm(s.url) === norm(href)));
      // 诊断日志：查看每个参考链接的 href 与匹配结果
      console.log('[LinkOpen] href =', href, '| sources.len =', sources.length, '| matched =', doc ? doc.title : 'NONE');
      if (doc && doc.downloadUrl) {
        // 首选真实 COS 下载链接：标记为文件链接，点击由前端拦截弹出操作面板（预览/复制下载链接）
        finalHref = doc.downloadUrl;
        token.attrSet('class', 'md-file-link');
        token.attrSet('data-download-url', doc.downloadUrl);
        token.attrSet('data-file-type', doc.fileType || '');
        token.attrSet('data-title', doc.title || '');
      } else if (doc && doc.url) {
        // 降级：补全乐享完整域名（doc.url 自带 pages/ 前缀，直接拼接，勿重复加 /）
        finalHref = LEXIANG_ORIGIN + doc.url;
      }
      // 未匹配到参考文档：finalHref 保持 null → 降级为纯文本，避免拼错域名 404
    } else {
      console.log('[LinkOpen] href =', href, '| external, keep as-is');
      finalHref = href;
    }
    if (finalHref) {
      token.attrSet('href', finalHref);
      token.attrSet('target', '_blank');
      token.attrSet('rel', 'noopener noreferrer');
    } else {
      // 纯文本降级：<a> 换成 <span>，移除 href，仅显示标题、不可点击
      token.tag = 'span';
      const hrefIdx = token.attrIndex('href');
      if (hrefIdx !== -1) token.attrs.splice(hrefIdx, 1);
      for (let i = idx + 1; i < tokens.length; i += 1) {
        if (tokens[i].type === 'link_close') {
          tokens[i].tag = 'span';
          break;
        }
      }
    }
  }
  return defaultLinkOpen(tokens, idx, options, env, self);
};

export default {
  data() {
    return {
      activeTab: 1,

      // AI搜索（代码保留，隐藏入口）
      searchMessages: [],
      searchLoading: false,
      searchInput: '',

      // AI问答
      qaMessages: [],
      qaLoading: false,
      qaInput: '',
      sessionId: null,
      isNewSession: true,
      selectedQaMode: 'react-ds-v4-flash',
      showModelSheet: false,
      // 流式状态
      streaming: false,
      streamFails: 0,

      // SSO 模式（路由 /SSO）：FAQ 独立配置、走 /lexiang/ai/sso/stream、不展示任何文件链接
      ssoMode: false,

      // 常见问题
      faqList: [],
      showFaq: true,
      hasReceivedFirstAiReply: false,
      faqLoading: true,
      // FAQ 迷你条左右箭头显隐
      faqArrowLeft: false,
      faqArrowRight: false,
      qaModes: [
        {
          group: 'AI 模型',
          items: [
            { name: 'react-hy3', label: 'HY 3.0' },
            { name: 'react-glm-5.2', label: 'GLM-5.2' },
            { name: 'react-glm-5v-turbo', label: 'GLM-5V-Turbo' },
            { name: 'react-glm-5', label: 'GLM-5.0' },
            { name: 'react-kimi-k2.6', label: 'Kimi K2.6' },
            { name: 'react-kimi-k2.5', label: 'Kimi K2.5' },
            { name: 'react-ds-v4-flash', label: 'DeepSeek V4-Flash' },
            { name: 'react-ds-v4-pro', label: 'DeepSeek V4-Pro' },
          ],
        },
      ],

      // 文件查看
      showFileViewer: false,
      currentFile: null,
      currentFileUrl: '',
      currentFileTitle: '',
      currentFileType: '',
      videoError: false,
      videoFallbackUsed: false,

      // 主题（light / dark），跟随系统偏好并记忆用户选择
      theme: 'light',

      // 语言（zh / en），localStorage 记忆（tek-locale）
      locale: DEFAULT_LOCALE,

      // 导航栏二级菜单：⋮ 更多（主题切换 / AI 模型）、语言选择
      showMoreSheet: false,
      showLocaleSheet: false,

      // 引用片段弹层（点击 AI 回答中的引用上标打开）
      showRefPopup: false,
      refPopupSource: null,
      refPopupChunk: null,

      // 正文文档链接操作面板（点击正文中的文档链接打开）
      showLinkSheet: false,
      linkSheetTarget: null,
    };
  },

  created() {
    // SSO 模式由路由 meta 区分：/SSO 路由 → ssoMode = true
    this.ssoMode = !!(this.$route && this.$route.meta && this.$route.meta.sso);
    this.initTheme();
    this.initLocale();
    this.loadFaqData();
    // 恢复上次会话上下文：刷新页面后多轮对话仍连续（最新会话时间超过 1 整天则视为过期，不恢复旧会话）
    // SSO 模式使用独立会话 key，避免与 AI 问答页会话串号
    try {
      if (!this.isSessionExpired()) {
        const saved = localStorage.getItem(this.ssoMode ? SESSION_KEY_SSO : 'tek-qa-session');
        if (saved) {
          this.sessionId = saved;
          this.isNewSession = false;
        }
      }
    } catch (e) {
      // localStorage 不可用时忽略
    }
    // 流式内部状态（非响应式，避免 Vue2 data 代理坑）
    this._abortCtrl = null;
    this._pendingDelta = '';
    this._streamMsg = null;
    this._streamRafId = null;
  },

  mounted() {
    // 监听浏览器后退事件，在文件预览浮层打开时拦截后退，改为关闭预览
    this._onPopState = () => {
      if (this.showFileViewer) {
        this._popstateTriggered = true;
        this.closeFileViewer();
      }
    };
    window.addEventListener('popstate', this._onPopState);
  },

  beforeDestroy() {
    if (this._onPopState) {
      window.removeEventListener('popstate', this._onPopState);
    }
  },

  watch: {
    theme(val) {
      document.documentElement.setAttribute('data-theme', val);
    },
    // 消息数量变化（首条对话出现输入栏时）触发箭头初始化
    qaMessagesCount() {
      this.$nextTick(() => this.updateFaqArrows());
    },
  },

  computed: {
    // 消息数量变化时触发 FAQ 迷你条箭头状态更新（仅在数量变化时，不随流式逐字刷新）
    qaMessagesCount() {
      return this.qaMessages.length;
    },
    // URL ?qa=aggregate 手动切回旧聚合接口（降级开关）
    forceAggregate() {
      try {
        return new URLSearchParams(window.location.search).get('qa') === 'aggregate';
      } catch (e) {
        return false;
      }
    },
    // 当前正在流式输出中的 AI 消息（无则 null）
    activeStreamMsg() {
      for (let i = this.qaMessages.length - 1; i >= 0; i -= 1) {
        const m = this.qaMessages[i];
        if (m.role === 'ai' && m.streaming) return m;
      }
      return null;
    },
    modelActions() {
      const actions = [];
      this.qaModes.forEach((group) => {
        // 分组标题
        actions.push({
          name: this.$t('model.group'),
          disabled: true,
          className: 'action-group-title',
        });
        group.items.forEach((m) => {
          actions.push({
            name: m.label,
            value: m.name,
            subname: m.name === this.selectedQaMode ? this.$t('model.current') : '',
            className: m.name === this.selectedQaMode ? 'action-item-selected' : '',
          });
        });
      });
      return actions;
    },
    // 语言选择 action sheet 选项（语言名保持原样不翻译）
    localeActions() {
      return [
        { name: this.$t('nav.zh'), value: 'zh', className: this.locale === 'zh' ? 'action-item-selected' : '' },
        { name: this.$t('nav.en'), value: 'en', className: this.locale === 'en' ? 'action-item-selected' : '' },
      ];
    },
    // ⋮ 更多二级菜单：主题切换 + AI 模型（AI 模型名不翻译）
    moreActions() {
      return [
        {
          name: this.$t('nav.theme'),
          subname: this.theme === 'dark' ? this.$t('nav.darkMode') : this.$t('nav.lightMode'),
          value: 'theme',
        },
        {
          name: this.$t('nav.model'),
          subname: this.getModelLabel(),
          value: 'model',
        },
      ];
    },
    fileIconFallback() {
      return this.currentFile ? this.getFileIcon(this.currentFile) : 'bulb-o';
    },
    // 正文文档链接操作面板选项：有下载链接提供「预览」「复制下载链接」，仅跳转链接提供「打开来源」
    linkActions() {
      const t = this.linkSheetTarget;
      if (!t) return [];
      if (t.downloadUrl) {
        return [
          { name: this.$t('linkAction.preview'), value: 'preview' },
          { name: this.$t('linkAction.copyLink'), value: 'copy' },
        ];
      }
      if (t.url) {
        return [{ name: this.$t('linkAction.open'), value: 'open' }];
      }
      return [];
    },
    // 操作面板标题：优先显示文档名
    linkSheetTitle() {
      const t = this.linkSheetTarget;
      return t && t.title ? t.title : this.$t('linkAction.title');
    },
  },

  methods: {
    /* ========== 主题（浅色 / 深色） ========== */
    initTheme() {
      const saved = localStorage.getItem('tek-theme');
      const prefersDark =
        window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.theme = saved || (prefersDark ? 'dark' : 'light');
      document.documentElement.setAttribute('data-theme', this.theme);
    },
    toggleTheme() {
      this.theme = this.theme === 'dark' ? 'light' : 'dark';
      localStorage.setItem('tek-theme', this.theme);
    },

    /* ========== 国际化（中 / 英） ========== */
    initLocale() {
      let locale = DEFAULT_LOCALE;
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (SUPPORTED_LOCALES.includes(saved)) locale = saved;
      } catch (e) {
        // localStorage 不可用时使用默认语言
      }
      this.locale = locale;
      this.applyHtmlLang();
    },
    setLocale(locale) {
      if (!SUPPORTED_LOCALES.includes(locale) || locale === this.locale) return;
      this.locale = locale;
      try {
        localStorage.setItem(STORAGE_KEY, locale);
      } catch (e) {
        // localStorage 不可用时仅保留内存态
      }
      this.applyHtmlLang();
      // 切换语言后 FAQ 需按新语言重新加载
      this.loadFaqData();
    },
    applyHtmlLang() {
      document.documentElement.setAttribute('lang', this.locale === 'en' ? 'en' : 'zh-CN');
    },
    // 模板/逻辑层统一取值入口：$t('nav.title') / this.$t('search.found', { count: n })
    $t(key, params) {
      return translate(this.locale, key, params);
    },
    onLocaleSelect(action) {
      this.showLocaleSheet = false;
      this.setLocale(action.value);
    },
    onMoreSelect(action) {
      this.showMoreSheet = false;
      if (action.value === 'theme') {
        this.toggleTheme();
      } else if (action.value === 'model') {
        this.$nextTick(() => {
          this.showModelSheet = true;
        });
      }
    },

    /* ========== FAQ 常见问题 ========== */
    async loadFaqData() {
      // SSO 模式加载独立 FAQ 配置（引导三个技能：服务价格查询 / 保修期内查询 / 维修进度查询）
      const FAQ_FILE = this.ssoMode ? 'faq-sso.json' : 'faq.json';
      const FAQ_URL = process.env.NODE_ENV === 'production' ? '/webaikn/' + FAQ_FILE : '/' + FAQ_FILE;
      try {
        const resp = await fetch(FAQ_URL);
        if (!resp.ok) {
          console.error('FAQ 加载失败: HTTP', resp.status);
          return;
        }
        const data = await resp.json();
        // 双语结构 { zh: [...], en: [...] }；兼容旧版纯数组
        let list = Array.isArray(data) ? data : (data && (data[this.locale] || data.zh)) || [];
        this.faqList = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error('FAQ 加载异常:', e);
      } finally {
        this.faqLoading = false;
        this.$nextTick(() => this.updateFaqArrows());
      }
    },

    /* ========== FAQ 迷你条横向滚动控制 ========== */
    scrollFaqMini(dir) {
      const el = this.$refs.faqMiniScroll;
      if (!el) return;
      const scrollAmount = el.clientWidth * 0.7;
      el.scrollBy({ left: dir === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    },
    updateFaqArrows() {
      const el = this.$refs.faqMiniScroll;
      if (!el) {
        this.faqArrowLeft = false;
        this.faqArrowRight = false;
        return;
      }
      this.faqArrowLeft = el.scrollLeft > 2;
      this.faqArrowRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    },

    /* ========== Tab 切换 ========== */
    switchTab(index) {
      this.activeTab = index;
      const ref = index === 0 ? 'searchMsgRef' : 'qaMsgRef';
      this.$nextTick(() => this.scrollBottom(ref));
    },

    /* ========== AI搜索 ========== */
    async sendSearch() {
      const q = this.searchInput.trim();
      if (!q || this.searchLoading) return;

      this.searchMessages.push({ role: 'user', content: q });
      this.searchInput = '';
      this.searchLoading = true;
      this.$nextTick(() => this.scrollBottom('searchMsgRef'));

      try {
        const res = await aiSearch(q);
        const list = res.list || [];
        this.searchMessages.push({
          role: 'ai',
          content: list.length
            ? this.$t('search.found', { count: list.length })
            : this.$t('search.notFound'),
          files: list.map((item) => ({
            title: item.title || this.$t('search.unnamedFile'),
            description: item.content || '',
            url: item.url || '',
            downloadUrl: item.downloadUrl || '',
            fileType: item.fileType || '',
          })),
        });
      } catch (e) {
        this.searchMessages.push({
          role: 'ai',
          content: this.$t('toast.searchFailed') + (e.message || this.$t('toast.networkError')),
          files: [],
        });
      } finally {
        this.searchLoading = false;
        this.$nextTick(() => this.scrollBottom('searchMsgRef'));
      }
    },

    /* ========== AI问答 ========== */
    async sendQA() {
      const q = this.qaInput.trim();
      if (!q || this.qaLoading) return;
      await this.executeQARequest(q);
    },

    /* 点击常见问题直接发送（不走输入框） */
    async sendFAQQuestion(question) {
      if (!question || this.qaLoading) return;
      await this.executeQARequest(question);
    },

    /* AI问答核心请求逻辑（供 sendQA / sendFAQQuestion 复用） */
    async executeQARequest(queryText) {
      this.qaMessages.push({ role: 'user', content: queryText });
      this.qaInput = '';
      this.qaLoading = true;
      this.$nextTick(() => this.scrollBottom('qaMsgRef'));

      // 会话过期校验：最新会话时间为空或距当前超过 1 整天，则本次不携带会话 ID（置空），重新开新会话
      if (this.isSessionExpired()) {
        this.sessionId = null;
        this.isNewSession = true;
      }
      const params = {
        query: queryText,
        qaMode: this.selectedQaMode,
      };
      if (!this.isNewSession && this.sessionId) {
        params.sessionId = this.sessionId;
      }

      // SSO：强制走流式接口（后端无 sso 聚合通道），不降级；其余场景：
      // 降级条件：URL ?qa=aggregate 手动开关，或流式连续失败 2 次自动回退
      if (this.ssoMode) {
        await this.execStreamQA(params);
      } else if (this.forceAggregate || this.streamFails >= 2) {
        await this.execAggregateQA(params);
      } else {
        await this.execStreamQA(params);
      }

      this.qaLoading = false;
      this.$nextTick(() => this.scrollBottom('qaMsgRef'));
    },

    /* 流式问答：push 占位消息 → 逐块打字机输出 → done 后完整 Markdown 渲染 */
    async execStreamQA(params) {
      const msg = {
        role: 'ai',
        content: '',
        sources: [],
        chunks: [],
        streaming: true,  // 流式输出中：纯文本 + 光标
        completed: false, // 收到 done：完整 Markdown 渲染
      };
      this.qaMessages.push(msg);
      this.streaming = true;
      this._streamMsg = msg;
      this._pendingDelta = '';
      this._abortCtrl = new AbortController();

      // 提交累积的增量文本（rAF 帧内调用）
      const commitDelta = () => {
        if (this._pendingDelta) {
          msg.content += this._pendingDelta;
          this._pendingDelta = '';
        }
      };

      try {
        const { interrupted } = await askStream({
          ...params,
          // SSO 模式：统一走 /lexiang/ai/qa/stream，hideDocs=true 由后端屏蔽乐享链接与 downloadUrl
          hideDocs: this.ssoMode,
          signal: this._abortCtrl.signal,
          onContent: (delta) => {
            // rAF 节流合并，避免逐字触发 Vue diff 导致卡顿
            this._pendingDelta += delta;
            if (this._streamRafId == null) {
              this._streamRafId = requestAnimationFrame(() => {
                this._streamRafId = null;
                commitDelta();
                this.$nextTick(() => this.scrollBottom('qaMsgRef'));
              });
            }
          },
          onDocs: (docs, chunks) => {
            if (this.ssoMode) {
              // SSO：技能文件含敏感信息，不展示任何来源文档/文件链接；
              // 仅保留引用片段文本（title/content），url/downloadUrl/fileType 一律过滤
              msg.sources = [];
              msg.chunks = (chunks || []).map((c) => ({
                title: c.title || '',
                content: c.content || '',
              }));
            } else {
              // 参考文档/引用片段到达即渲染，不等流结束
              msg.sources = (docs || []).map((doc) => ({
                title: doc.title,
                url: doc.url,
                downloadUrl: doc.downloadUrl || '',
                fileType: doc.fileType || '',
              }));
              msg.chunks = chunks || [];
            }
            this.$nextTick(() => this.scrollBottom('qaMsgRef'));
          },
          onDone: (content, threadId) => {
            // done 已携带最终完整内容并整体覆盖 msg.content，
            // 必须取消挂起的 rAF 并清空待提交增量，否则残留的 _pendingDelta 会在 finally 被再次追加造成整段重复
            if (this._streamRafId != null) {
              cancelAnimationFrame(this._streamRafId);
              this._streamRafId = null;
            }
            this._pendingDelta = '';
            // 接口结束后输出最终返回内容，便于排查问题
            console.log('[QA-Stream-Done] content =', content);
            console.log('[QA-Stream-Done] threadId =', threadId);
            console.log('[QA-Stream-Done] sources =', JSON.stringify(msg.sources, null, 2));
            console.log('[QA-Stream-Done] chunks =', JSON.stringify(msg.chunks, null, 2));
            msg.content = content;
            msg.streaming = false;
            msg.completed = true;
            this.streaming = false;
            this.streamFails = 0;
            if (threadId) this.saveSession(threadId);
            this.afterFirstAiReply();
          },
          onError: (message) => {
            // SSE error 事件：保留已输出内容
            msg.streaming = false;
            msg.completed = false;
            this.streaming = false;
            this.streamFails += 1;
            this.$toast(message || this.$t('toast.answerError'));
          },
        });

        // 流正常结束但 EOF 未收到 done/error：断流中断
        if (interrupted) {
          msg.streaming = false;
          msg.completed = false;
          this.streaming = false;
          this.streamFails += 1;
          this.$toast(this.$t('toast.answerIncomplete'));
        }
      } catch (e) {
        if (e && e.name === 'AbortError') {
          // 用户点击停止：保留已输出内容，不提示错误
          msg.streaming = false;
          msg.completed = false;
          this.streaming = false;
        } else {
          // 网络异常 / HTTP 非 200
          msg.streaming = false;
          msg.completed = false;
          this.streaming = false;
          this.streamFails += 1;
          console.error('流式问答失败:', e);
          this.$toast(this.$t('toast.answerInterrupted'));
        }
      } finally {
        // 正常完成(done)时 onDone 已用完整 content 整体覆盖 msg.content，
        // 禁止再追加 _pendingDelta（否则整段重复）；
        // 仅中断场景（error/停止/断流，completed=false）才提交最后残留 delta，保留已输出内容
        if (!msg.completed) {
          commitDelta();
        }
        this._streamMsg = null;
        this._abortCtrl = null;
        if (this._streamRafId != null) {
          cancelAnimationFrame(this._streamRafId);
          this._streamRafId = null;
        }
      }
    },

    /* 聚合问答（降级通道）：一次性返回，响应结构与旧版一致 */
    async execAggregateQA(params) {
      try {
        const res = await aiQA(params);
        // 接口结束后输出最终返回内容，便于排查问题
        console.log('[QA-Aggregate-Done] response =', JSON.stringify(res, null, 2));
        if (res.sessionId) this.saveSession(res.sessionId);
        this.qaMessages.push({
          role: 'ai',
          content: res.content || this.$t('toast.noAnswer'),
          sources: (res.referenceDocs || []).map((doc) => ({
            title: doc.title,
            url: doc.url,
            downloadUrl: doc.downloadUrl || '',
            fileType: doc.fileType || '',
          })),
          // 引用片段，与答案中 <span data-ref-chunks="M"> 的 M 对应（1-based）
          chunks: res.referenceChunks || [],
          streaming: false,
          completed: true,
        });
        this.afterFirstAiReply();
      } catch (e) {
        this.qaMessages.push({
          role: 'ai',
          content: this.$t('toast.qaFailed') + (e.message || this.$t('toast.networkError')),
          sources: [],
          streaming: false,
          completed: true,
        });
      }
    },

    /* 停止流式生成：中断请求，保留已输出内容，恢复输入区 */
    stopGeneration() {
      if (this._abortCtrl) {
        this._abortCtrl.abort();
      }
    },

    /* 判断会话是否过期：最新会话时间为空，或距当前超过 1 整天（24h）即视为过期 */
    isSessionExpired() {
      try {
        const ts = Number(localStorage.getItem(this.ssoMode ? SESSION_TIME_KEY_SSO : SESSION_TIME_KEY));
        if (!ts) return true;
        return Date.now() - ts > SESSION_MAX_AGE;
      } catch (e) {
        // localStorage 不可用时视为过期，行为安全
        return true;
      }
    },

    /* 保存会话 ID：内存 + localStorage，刷新后上下文仍连续 */
    saveSession(threadId) {
      if (!threadId) return;
      this.sessionId = threadId;
      this.isNewSession = false;
      try {
        localStorage.setItem(this.ssoMode ? SESSION_KEY_SSO : 'tek-qa-session', threadId);
        // 同步刷新最新会话时间（毫秒时间戳），用于会话过期校验
        localStorage.setItem(this.ssoMode ? SESSION_TIME_KEY_SSO : SESSION_TIME_KEY, String(Date.now()));
      } catch (e) {
        // localStorage 不可用时仅保留内存态
      }
    },

    /* 首次 AI 回答后隐藏常见问题模块 */
    afterFirstAiReply() {
      if (!this.hasReceivedFirstAiReply) {
        this.hasReceivedFirstAiReply = true;
        this.showFaq = false;
      }
    },

    /* ========== Markdown 渲染与引用跳转 ========== */
    renderMarkdown(content, sources) {
      if (!content) return '';
      try {
        // 后端返回的引用标签使用 id="ai-qa-ref"，需改为 class 才能命中样式与事件委托
        const preprocessed = content.replace(
          /<span\s+id\s*=\s*["']ai-qa-ref["']/gi,
          '<span class="ai-qa-ref"'
        );
        // env 携带 sources：link_open 规则据此把参考文档相对路径链接替换为真实下载链接；
        // SSO 模式传 sso 标志：link_open 将所有链接降级为纯文本，不展示文件链接
        const html = md.render(preprocessed, { sources: sources || [], sso: this.ssoMode });
        // DOMPurify 白名单兜底，防 XSS；data-ref-* 属性用于引用上标跳转
        return DOMPurify.sanitize(html, {
          ADD_ATTR: [
            'target', 'rel',
            'data-ref-doc', 'data-ref-chunks', 'data-completion-id',
            'data-download-url', 'data-file-type', 'data-title',
          ],
        });
      } catch (e) {
        // 渲染失败时退回纯文本转义，保证内容可见
        return md.utils.escapeHtml(content);
      }
    },

    /* 点击 AI 回答正文：文档链接弹操作面板，引用上标弹引用片段 */
    onMsgTextClick(e, msg) {
      const target = e.target;
      if (!target || !target.closest) return;
      // 文档链接：阻止默认下载，弹出「预览 / 复制下载链接」操作面板
      const link = target.closest('a.md-file-link');
      if (link) {
        e.preventDefault();
        e.stopPropagation();
        this.linkSheetTarget = {
          title: link.getAttribute('data-title') || '',
          downloadUrl: link.getAttribute('data-download-url') || link.getAttribute('href') || '',
          fileType: link.getAttribute('data-file-type') || '',
          url: '',
        };
        this.showLinkSheet = true;
        return;
      }
      const span = target.closest('.ai-qa-ref');
      if (!span) return;
      const docIdx = parseInt(span.getAttribute('data-ref-doc'), 10);
      const chunkIdx = parseInt(span.getAttribute('data-ref-chunks'), 10);
      const sources = msg.sources || [];
      const chunks = msg.chunks || [];
      const source = sources[docIdx - 1];
      const chunk = chunks[chunkIdx - 1];
      if (!source && !chunk) {
        this.$toast(this.$t('toast.refNotFound'));
        return;
      }
      this.refPopupSource = source || null;
      this.refPopupChunk = chunk || null;
      this.showRefPopup = true;
    },

    /* 正文文档链接操作面板：预览 / 复制下载链接 */
    onLinkSheetSelect(action) {
      this.showLinkSheet = false;
      const t = this.linkSheetTarget;
      if (!t) return;
      if (action.value === 'preview') {
        this.viewFile(t);
      } else if (action.value === 'copy') {
        this.copyDownloadLink(t);
      } else if (action.value === 'open') {
        this.openUrl(t.url);
      }
    },

    /* 在引用弹层中查看来源文档 */
    viewRefSource() {
      this.showRefPopup = false;
      const src = this.refPopupSource;
      if (src && src.downloadUrl) {
        this.viewFile(src);
      } else if (src && src.url) {
        this.openUrl(src.url);
      }
    },

    /* ========== 文件查看 ========== */
    viewFile(file) {
      if (!file || !file.downloadUrl) {
        this.$toast(this.$t('toast.noDownloadLink'));
        return;
      }
      this.currentFile = file;
      this.currentFileTitle = file.title || this.$t('viewer.filePreview');
      this.currentFileType = this.detectType(file.fileType || '');
      // 通过 nginx 代理访问文件，解决 CORS 跨域问题，
      // 同时 nginx 会隐藏 Content-Disposition 头，使浏览器内联预览而非下载
      let url = file.downloadUrl;
      if (url.startsWith('https://file.lexiang-asset.com/')) {
        url = url.replace('https://file.lexiang-asset.com/', '/webaikn/file-proxy/');
      } else if (url.startsWith('https://video.lexiang-asset.com/')) {
        url = url.replace('https://video.lexiang-asset.com/', '/webaikn/video-proxy/');
      }
      this.currentFileUrl = url;
      this.videoError = false;
      this.videoFallbackUsed = false;
      this.showFileViewer = true;
      // 向浏览器历史栈推入一条记录，使后退键先关闭预览而非退出页面
      history.pushState({ fileViewerOpen: true }, '', location.href);
    },

    closeFileViewer() {
      this.showFileViewer = false;
      this.currentFileUrl = '';
      this.videoError = false;
      this.videoFallbackUsed = false;
      if (this._popstateTriggered) {
        // 浏览器后退触发：历史栈已自动回退，仅重置标记
        this._popstateTriggered = false;
      } else {
        // 用户手动关闭（点击返回箭头 / 遮罩）：清理之前 push 的历史条目
        history.back();
      }
    },

    /* 视频播放失败：先尝试原始直链，仍失败则提示复制链接 */
    onVideoError() {
      if (this.currentFileType !== 'video') return;
      if (
        this.currentFileUrl.includes('/webaikn/video-proxy/') &&
        this.currentFile &&
        this.currentFile.downloadUrl &&
        !this.videoFallbackUsed
      ) {
        this.videoFallbackUsed = true;
        this.currentFileUrl = this.currentFile.downloadUrl;
        return;
      }
      this.videoError = true;
    },

    /* 下载：使用原始 COS 直链（含 response-content-disposition=attachment），触发浏览器下载 */
    downloadFile() {
      if (this.currentFile && this.currentFile.downloadUrl) {
        window.open(this.currentFile.downloadUrl, '_blank');
      }
    },

    /* 复制下载链接到剪贴板 */
    copyDownloadLink(file) {
      if (!file || !file.downloadUrl) {
        this.$toast(this.$t('toast.noDownloadLink'));
        return;
      }
      const url = file.downloadUrl;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          this.$toast(this.$t('toast.linkCopied'));
        }).catch(() => {
          this.fallbackCopy(url);
        });
      } else {
        this.fallbackCopy(url);
      }
    },

    fallbackCopy(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        this.$toast(this.$t('toast.linkCopied'));
      } catch (e) {
        this.$toast(this.$t('toast.copyFailed'));
      }
      document.body.removeChild(textarea);
    },

    /* ========== 模型 / 思考方式 ========== */
    onModelSelect(action) {
      this.selectedQaMode = action.value;
      this.showModelSheet = false;
    },

    getModelLabel() {
      for (const group of this.qaModes) {
        const m = group.items.find((item) => item.name === this.selectedQaMode);
        if (m) return m.label;
      }
      return this.selectedQaMode;
    },

    /* ========== 会话 ========== */
    startNewSession() {
      this.qaMessages = [];
      this.sessionId = null;
      this.isNewSession = true;
      this.qaInput = '';
      this.showFaq = true;
      this.hasReceivedFirstAiReply = false;
      this.showRefPopup = false;
      this.refPopupSource = null;
      this.refPopupChunk = null;
      try {
        localStorage.removeItem(this.ssoMode ? SESSION_KEY_SSO : 'tek-qa-session');
      } catch (e) {
        // localStorage 不可用时忽略
      }
    },

    /* ========== 工具 ========== */
    openUrl(url) {
      if (url) {
        // 乐享相对路径（/pages/...）补全完整域名；已是绝对地址则原样打开
        const fullUrl = url.startsWith('http') ? url : LEXIANG_ORIGIN + url;
        window.open(fullUrl, '_blank');
      }
    },

    shortId(id) {
      if (!id) return '';
      return id.length > 8 ? id.slice(0, 8) + '...' : id;
    },

    /* 文件类型中文标签 */
    getFileTypeLabel(fileType) {
      if (!fileType) return '';
      const ext = fileType.toLowerCase();
      if (['mp4', 'avi', 'mov', 'mkv', 'flv', 'wmv', 'webm'].includes(ext)) return this.$t('fileType.video');
      if (['mp3', 'wav', 'aac', 'ogg', 'flac', 'wma', 'm4a'].includes(ext)) return this.$t('fileType.audio');
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return this.$t('fileType.image');
      if (ext === 'pdf') return 'PDF';
      if (['doc', 'docx'].includes(ext)) return 'Word';
      if (['xls', 'xlsx', 'csv'].includes(ext)) return 'Excel';
      if (['ppt', 'pptx'].includes(ext)) return 'PPT';
      return ext.toUpperCase();
    },

    detectType(fileType) {
      if (!fileType) return 'other';
      const ext = fileType.toLowerCase();
      if (['mp4', 'avi', 'mov', 'mkv', 'flv', 'wmv', 'webm'].includes(ext)) return 'video';
      if (['mp3', 'wav', 'aac', 'ogg', 'flac', 'wma', 'm4a'].includes(ext)) return 'audio';
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return 'image';
      if (ext === 'pdf') return 'pdf';
      return 'other';
    },

    getFileIcon(file) {
      if (!file) return 'bulb-o';
      const t = this.detectType(file.fileType || '');
      return { video: 'video-o', audio: 'music-o', image: 'photo-o', pdf: 'description' }[t] || 'bulb-o';
    },

    getFileColor(file) {
      if (!file) return '#c8c9cc';
      const t = this.detectType(file.fileType || '');
      return { video: '#ee0a24', audio: '#ff976a', image: '#07c160', pdf: '#ee0a24' }[t] || '#c8c9cc';
    },

    scrollBottom(refName) {
      const el = this.$refs[refName];
      if (el) el.scrollTop = el.scrollHeight;
    },
  },
};
